import { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';

import {
  CustomButtonGroup,
  Input,
  IProfilePhotoUploadRef,
  Label,
  ProfilePhotoUpload,
} from '@/components/common';
import {
  ERROR_INVALID_EMAIL,
  ERROR_MAX_LENGTH,
  ERROR_REQUIRED_FIELD,
} from '@/constants/validationError.constants';

import {
  FACE_ID_PROFILE_DEPARTMENT_MAX_LENGTH,
  FACE_ID_PROFILE_EMAIL_MAX_LENGTH,
  getProfileStatusLabel,
  IEmployeeProfileUpdateSection,
  ProfileStatus,
} from '@/features/face-id-profiles';
import { EMAIL_VALID_CHARACTERS_REGEX, EMAIL_VALIDATION_REGEX } from '@/constants';

const EmployeeProfileSection = ({
  defaultValues,
  control,
  formFields,
  errors,
}: IEmployeeProfileUpdateSection) => {
  const photoUploadRef = useRef<IProfilePhotoUploadRef>(null);

  // Sync validation error to ProfilePhotoUpload
  useEffect(() => {
    const errorMessage = errors[formFields.photoId]?.message;
    if (errorMessage) {
      photoUploadRef.current?.setError(errorMessage);
    } else {
      photoUploadRef.current?.setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors[formFields.photoId]]);
  return (
    <>
      <Input
        label="Employee ID"
        placeholder="Employee ID"
        required
        disabled
        value={defaultValues?.employeeId}
      />
      <Controller
        name={formFields.department}
        control={control}
        rules={{
          maxLength: {
            value: FACE_ID_PROFILE_DEPARTMENT_MAX_LENGTH,
            message: ERROR_MAX_LENGTH(FACE_ID_PROFILE_DEPARTMENT_MAX_LENGTH),
          },
        }}
        render={({ field }) => (
          <Input
            label="Department/Team"
            placeholder="Department/Team"
            value={field.value}
            onChange={field.onChange}
            maxLength={FACE_ID_PROFILE_DEPARTMENT_MAX_LENGTH}
            error={errors[formFields.department]?.message}
          />
        )}
      />
      <Controller
        name={formFields.email}
        control={control}
        rules={{
          required: ERROR_REQUIRED_FIELD,
          maxLength: {
            value: FACE_ID_PROFILE_EMAIL_MAX_LENGTH,
            message: ERROR_MAX_LENGTH(FACE_ID_PROFILE_EMAIL_MAX_LENGTH),
          },
          pattern: { value: EMAIL_VALIDATION_REGEX, message: ERROR_INVALID_EMAIL },
        }}
        render={({ field }) => (
          <Input
            label="Email"
            placeholder="Email"
            required
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(
                e.target.value.toLocaleLowerCase().replace(EMAIL_VALID_CHARACTERS_REGEX, '').trim(),
              )
            }
            maxLength={FACE_ID_PROFILE_EMAIL_MAX_LENGTH}
            error={errors[formFields.email]?.message}
          />
        )}
      />
      <Controller
        name={formFields.photoId}
        control={control}
        rules={{ required: ERROR_REQUIRED_FIELD }}
        render={({ field }) => (
          <ProfilePhotoUpload
            ref={photoUploadRef}
            initialPhotoUrl={defaultValues?.photoUrl}
            initialPhotoFileName={defaultValues?.photoName}
            onUpdate={({ photoId }) => field.onChange(photoId)}
          />
        )}
      />
      <div className="flex flex-col gap-1.5 border-neutral-n4 w-full">
        <Label className="!gap-0 font-semibold">
          Status<span className="ml-1 text-semantic-error-base">*</span>
        </Label>
        <Controller
          name={formFields.status}
          control={control}
          render={({ field }) => (
            <CustomButtonGroup
              buttonNames={[
                { name: getProfileStatusLabel(ProfileStatus.Active), value: ProfileStatus.Active },
                {
                  name: getProfileStatusLabel(ProfileStatus.Inactive),
                  value: ProfileStatus.Inactive,
                },
              ]}
              selectedIndex={field.value as ProfileStatus}
              onSelect={(index) => field.onChange(index)}
            />
          )}
        />
      </div>
    </>
  );
};

export default EmployeeProfileSection;
